import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, UserRound, CassetteTape, Undo2 } from "lucide-react";
import { http } from "@/lib/api";
import { useGroup } from "@/lib/GroupContext";
import { Paper } from "@/components/Paper";
import { Ticks } from "@/components/Ticks";
import { VideoSlot } from "@/components/FootagePending";
import { Interrogation } from "@/components/Interrogation";
import { Breadcrumbs } from "@/components/Brand";

const TABS = [
  ["tape", "Listen to the cassette"],
  ["crosscheck", "Cross-check"],
  ["clue", "Search the asylum"],
  ["profile", "Profile"],
  ["interrogate", "Interview record"],
];

function CassetteTab({ info }) {
  const { state, markStep } = useGroup();
  const heard = state.suspects[info.id].progress.tape;
  return (
    <div className="max-w-3xl" data-testid="cassette-panel">
      <div className="banner">
        <div className="banner-head flex items-center gap-3"><CassetteTape size={22} /> Listen to the tapes on the cassette players</div>
        <div className="px-6 py-5 text-xl leading-relaxed">
          <p data-testid="cassette-instruction">Find the cassette marked <strong>{info.name} — Interview {info.tape.ref}</strong> and play it in full.</p>
          <p className="text-lg text-[#505a5f] mt-3">The recording is the Military Police interview with {info.name}. Listen closely: you will test it against the record in the next step.</p>
        </div>
      </div>
      {heard ? (
        <div className="mt-8 flex flex-wrap items-center gap-6" data-testid="cassette-heard">
          <strong className="tag tag-green text-lg !inline-flex items-center gap-2 whitespace-nowrap"><Check size={18} /> Cassette heard</strong>
          <button className="link text-lg font-bold flex items-center gap-1" onClick={() => markStep(info.id, "tape", false)} data-testid="cassette-undo-button"><Undo2 size={18} /> Undo: we haven't listened yet</button>
        </div>
      ) : (
        <button className="btn-spore mt-8" onClick={() => markStep(info.id, "tape", true)} data-testid="cassette-heard-button"><Check size={19} /> We've listened to this tape</button>
      )}
    </div>
  );
}

function CrossCheck({ info }) {
  const { state, markStep } = useGroup();
  const done = state.suspects[info.id].progress.crosscheck;
  useEffect(() => {
    if (!done) markStep(info.id, "crosscheck");
  }, [done, info.id, markStep]);
  return (
    <div>
      <h2 className="text-3xl font-black">Cross-check the record</h2>
      <p className="text-lg text-[#505a5f] mt-2 mb-6">Test what {info.name} said on tape against this document.</p>
      <div className="doc-stage"><Paper doc={info.crosscheck} testid="crosscheck-document" /></div>
    </div>
  );
}

function ClueTab({ info }) {
  const { state, setLockerOpen } = useGroup();
  const s = state.suspects[info.id];
  if (!s.clue) {
    return (
      <div className="max-w-3xl" data-testid="clue-locked-panel">
        <h2 className="text-3xl font-black">Search the asylum</h2>
        <p className="text-xl mt-4 leading-relaxed">
          A document belonging to {info.name}'s file is hidden somewhere in the building. It carries a 4-digit RECOVERY CODE.
        </p>
        <div className="inset mt-6 text-lg">Find it, then submit the code as an evidence reference to add the document to your case file.</div>
        <button className="btn-spore mt-8" onClick={() => setLockerOpen(true)} data-testid="clue-open-locker-button">Submit evidence reference</button>
      </div>
    );
  }
  return (
    <div data-testid="clue-unlocked-panel">
      <div className="banner success max-w-3xl mb-8">
        <div className="banner-head">Evidence recovered</div>
        <p className="px-5 py-4 text-xl font-bold">{s.clue.title} has been added to your case file.</p>
      </div>
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 2xl:col-span-8 doc-stage"><div className="glitch-reveal"><Paper doc={s.clue} testid="clue-document" /></div></div>
        <div className="col-span-12 2xl:col-span-4">
          <VideoSlot slot={s.video} label={`Damning footage · ${info.name}`} testid="damning-video-slot" />
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ info, goInterrogate }) {
  const { state, markStep } = useGroup();
  const read = state.suspects[info.id].progress.profile;
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <div className="doc-stage"><Paper doc={info.profile} testid="profile-document" /></div>
      <div className="mt-8 flex items-center gap-5">
        {read ? (
          <>
            <span className="flex items-center gap-2 text-xl font-bold text-[#00703c]" data-testid="profile-read-confirmed"><Check /> Profile read</span>
            <button className="btn-spore ml-auto" onClick={goInterrogate} data-testid="profile-go-interrogate-button">
              {info.mode === "ai" ? "Begin the interview" : "Question him in the room"} <ChevronRight size={20} />
            </button>
          </>
        ) : (
          <button className="btn-spore" disabled={busy} onClick={async () => { setBusy(true); await markStep(info.id, "profile"); setBusy(false); }} data-testid="profile-confirm-button">
            <Check size={20} /> I've read the profile
          </button>
        )}
      </div>
    </div>
  );
}

function LiveWitness({ info }) {
  const { state } = useGroup();
  const s = state.suspects[info.id];
  return (
    <div className="grid grid-cols-12 gap-8" data-testid="live-witness-panel">
      <div className="col-span-12 xl:col-span-7 banner">
        <div className="banner-head flex items-center gap-2"><UserRound size={18} /> Live witness · in the room</div>
        <div className="p-10">
          <h2 className="text-4xl font-black">Question Dr Kit Marsh in person</h2>
          <p className="text-xl leading-relaxed mt-5">
            Dr Marsh has returned to the site as a technical witness. There is no recording to question here. Find him in the room and put your questions to him directly.
          </p>
          <blockquote className="inset mt-8 font-bold text-3xl leading-snug" data-testid="marsh-opening-line">"{info.opening_line}"</blockquote>
          <p className="caption mt-3">His opening line</p>
        </div>
      </div>
      <div className="col-span-12 xl:col-span-5 panel p-10">
        <h2 className="text-2xl font-black">Show him the evidence</h2>
        <p className="text-lg leading-relaxed mt-4">
          Bring him his own records and ask what they show. When you recover his hidden clue, show it to him in the room. It is logged in your case notes as discussed.
        </p>
        <p className={`mt-6 text-xl font-bold ${s.progress.secret ? "text-[#00703c]" : "text-[#505a5f]"}`} data-testid="marsh-secret-status">
          {s.progress.secret ? "Hidden clue recovered — discussed with the witness" : "Hidden clue not yet recovered"}
        </p>
      </div>
    </div>
  );
}

function InterrogateTab({ info, goProfile }) {
  const { state } = useGroup();
  if (info.mode !== "ai") return <LiveWitness info={info} />;
  if (!state.suspects[info.id].progress.profile) {
    return (
      <div className="max-w-3xl" data-testid="interrogation-locked">
        <h2 className="text-3xl font-black">Interview locked</h2>
        <div className="warning-text mt-5">
          <span className="warning-icon" aria-hidden>!</span>
          <strong>Read {info.name}'s profile and confirm "I've read the profile" before you question them.</strong>
        </div>
        <button className="btn-spore mt-8" onClick={goProfile} data-testid="interrogation-go-profile-button">Read the profile</button>
      </div>
    );
  }
  return <Interrogation suspect={info} />;
}

export default function Station() {
  const { sid } = useParams();
  const { state } = useGroup();
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const tab = params.get("tab") || "tape";

  useEffect(() => {
    setInfo(null);
    http.get(`/suspects/${sid}`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [sid]);

  if (info === false) return <Navigate to="/" replace />;
  if (!state || !info) return <p className="py-10 text-lg">Opening case file page…</p>;

  const s = state.suspects[sid];
  const setTab = (t) => setParams({ tab: t });

  return (
    <div data-testid={`station-${sid}`}>
      <Breadcrumbs items={[["Suspect interviews", "/"], [info.name]]} />
      <div className="flex items-start justify-between gap-8 mt-8 mb-8">
        <div>
          <span className="caption">Case file page · Suspect interview</span>
          <h1 className="text-5xl font-black mt-2" data-testid="station-name">{info.name}</h1>
          <p className="text-xl text-[#505a5f] mt-2">{info.role}</p>
        </div>
        <div className="panel px-6 py-4"><Ticks sid={sid} progress={s.progress} compact /></div>
      </div>

      <div className="tabs-list mb-10" role="tablist">
        {TABS.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`tab ${tab === key ? "selected" : ""}`} data-testid={`station-tab-${key}`}>
            {key === "interrogate" && info.mode !== "ai" ? "Question in the room" : label}
          </button>
        ))}
      </div>

      {tab === "tape" && <CassetteTab key={sid} info={info} />}
      {tab === "crosscheck" && <CrossCheck info={info} />}
      {tab === "clue" && <ClueTab info={info} />}
      {tab === "profile" && <ProfileTab info={info} goInterrogate={() => setTab("interrogate")} />}
      {tab === "interrogate" && <InterrogateTab info={info} goProfile={() => setTab("profile")} />}

      <div className="flex justify-between mt-14 pt-6 border-t border-[#b1b4b6]">
        <Link to="/" className="link text-xl font-bold flex items-center gap-1" data-testid="back-to-suspects-link"><ChevronLeft size={20} /> All suspect interviews</Link>
        <Link to="/charge" className="link text-xl font-bold flex items-center gap-1" data-testid="to-accusation-link">Charge decision <ChevronRight size={20} /></Link>
      </div>
    </div>
  );
}
